import React, { FC } from 'react'
import { FormikProps } from 'formik'
import { Heading, Hr, Paragraph } from '@pandiafi/design-system'
import { ContextNotification } from '@pandiafi/components'
import { useTranslation } from 'react-i18next'

import {
  Contract,
  ContractTenant,
  ModifyContractInput,
  modifyContractInputSchema,
  UserMetadata,
} from '../../../../common/validations'
import { validateFormValues } from '../../../utils'
import { DATE_FORMATS } from '../../../utils/dateUtils'
import { ModificationFormLayout } from '../../ContractChanges/ModificationForm/ModificationFormLayout/ModificationFormLayout'
import { ModificationFormButtons } from '../../ContractChanges/ModificationForm/ModificationFormButtons/ModificationFormButtons'
import { TenantSelectionNotification } from '../../ContractChanges/ModificationForm/TenantSelectionNotification/TenantSelectionNotification'
import { ModificationInEffectField } from '../../ContractChanges/ModificationForm/ModificationInEffectField/ModificationInEffectField'
import { ModificationDescriptionField } from '../../ContractChanges/ModificationForm/ModificationDescriptionField/ModificationDescriptionField'
import {
  TenantChoice,
  TenantChoiceState,
} from '../../ContractChanges/components/TenantChoice/TenantChoice'
import { lockChoiceByTenantStatus } from '../../ContractChanges/components/TenantChoice/lockChoiceByTenantStatus'
import { TerminateSuggestionAction } from '../../ContractChanges/hooks/useTerminateSuggestions'
import { useModifyContractForm } from '../../ContractChanges/hooks/useModifyContractForm'
import { resolveContractTerminationTerminology } from '../../ContractChanges/utils/resolveContractTerminationTerminology'
import { ContractChangesViewState } from '../../ContractChanges/types'
import { isMoveOutDisabled } from '../../ContractChanges/utils/isMoveOutDisabled'

import { useContractPaths } from 'hooks/useContractPaths/useContractPaths'
import { StyledLink } from 'components/StyledLink/StyledLink'

type Props = {
  view: ContractChangesViewState
  setView: (view: ContractChangesViewState) => void
  contract: Contract
  tenants: ContractTenant[]
  userMetadata: UserMetadata
  onCancel: () => void
}

export const MoveOutForm: FC<Props> = ({
  view,
  setView,
  contract,
  tenants,
  userMetadata,
  onCancel,
}) => {
  const { t } = useTranslation(['contractModification'])
  const { terminateContractPath } = useContractPaths()
  const {
    inEffectDisabledBefore,
    inEffectDisabledAfter,
    movingOutTenantIds,
    initialValues,
    changeMovingOutTenants,
    showPreview,
    cancel,
  } = useModifyContractForm({ contract, tenants, setView, onCancel })

  const terminology = resolveContractTerminationTerminology(
    contract.contractType
  )

  const handleValidate = (form: ModifyContractInput) =>
    validateFormValues(form, modifyContractInputSchema)

  const handleTenantChange = (
    form: FormikProps<ModifyContractInput>,
    tenant: ContractTenant,
    suggestionId: string | undefined
  ) =>
    changeMovingOutTenants(
      form,
      suggestionId === TerminateSuggestionAction.TERMINATE
        ? [...movingOutTenantIds, tenant.id]
        : movingOutTenantIds.filter((tenantId) => tenantId !== tenant.id)
    )

  const resolveTenantChoice = (
    form: FormikProps<ModifyContractInput>,
    tenant: ContractTenant
  ): TenantChoiceState => {
    if (tenant.isContractParty) {
      return {
        type: 'locked',
        label: t('contractModification:tenantChoices.contractParty'),
      }
    }

    return {
      type: 'select',
      suggestions: [
        {
          id: TerminateSuggestionAction.IN_EFFECT,
          label: t('contractModification:tenantChoices.lives'),
          disabled: false,
        },
        {
          id: TerminateSuggestionAction.TERMINATE,
          label: t('contractModification:tenantChoices.movesOut'),
          disabled: isMoveOutDisabled(contract, tenant),
        },
      ],
      value: movingOutTenantIds.includes(tenant.id)
        ? TerminateSuggestionAction.TERMINATE
        : TerminateSuggestionAction.IN_EFFECT,
      onChange: (suggestionId) =>
        handleTenantChange(form, tenant, suggestionId),
    }
  }

  return (
    <ModificationFormLayout
      view={view}
      setView={setView}
      contract={contract}
      tenants={tenants}
      userMetadata={userMetadata}
      initialValues={initialValues}
      validate={handleValidate}
    >
      {(form, genericError) => (
        <>
          <Heading element="h3" size="l" className="mb-4 font-medium">
            {t('contractModification:headings.whoMovesOut')}
          </Heading>

          <Paragraph large className="mb-6">
            {t('contractModification:texts.selectMovingOutTenants')}
          </Paragraph>

          {tenants.map((tenant) => (
            <TenantChoice
              key={tenant.id}
              tenant={tenant}
              isCurrentUser={tenant.id === contract.tenant.id}
              choice={lockChoiceByTenantStatus(
                contract,
                tenants,
                tenant,
                resolveTenantChoice(form, tenant)
              )}
            />
          ))}

          <ContextNotification type="info" textAlign="center" className="mt-4">
            <StyledLink to={terminateContractPath} className="text-center">
              {t(terminology.moveOutContractPartyInfo)}
            </StyledLink>
          </ContextNotification>

          {movingOutTenantIds.length > 0 && (
            <>
              <div className="mt-6">
                <TenantSelectionNotification
                  otherContractPartySelected={false}
                  genericError={genericError}
                />
              </div>

              <Hr className="my-10 border-common-gray-4" />

              <Heading size="l" element="h3" className="mb-4 font-medium">
                {t('contractModification:headings.tenantLeaves', {
                  count: movingOutTenantIds.length,
                })}
              </Heading>

              <ModificationInEffectField
                form={form}
                description={t('contractModification:texts.moveOutDateRange', {
                  start: inEffectDisabledBefore.toFormat(
                    DATE_FORMATS.LOCALE_DATE
                  ),
                  end: inEffectDisabledAfter.toFormat(DATE_FORMATS.LOCALE_DATE),
                })}
                allowedDates={undefined}
                disabledBefore={inEffectDisabledBefore}
                disabledAfter={inEffectDisabledAfter}
              />

              <ModificationDescriptionField form={form} />

              <ModificationFormButtons
                onCancel={() => cancel(form)}
                onPreview={() => showPreview(form)}
                previewDisabled={form.isSubmitting || Boolean(genericError)}
              />
            </>
          )}
        </>
      )}
    </ModificationFormLayout>
  )
}
